import { supabase } from '../lib/supabase';

export const voiceActorService = {
  async getAllVoiceActors() {
    try {
      const { data, error } = await supabase?.from('voice_actors')?.select(`
          *,
          user_profiles!user_id (
            full_name,
            email
          )
        `)?.order('stage_name');

      if (error) throw error;

      return data?.map(actor => ({
        id: actor?.id,
        userId: actor?.user_id,
        stageName: actor?.stage_name,
        bio: actor?.bio,
        headshotUrl: actor?.headshot_url,
        vocalRange: actor?.vocal_range,
        yearsExperience: actor?.years_experience,
        specializations: actor?.specializations || [],
        demoReelUrl: actor?.demo_reel_url,
        hourlyRate: actor?.hourly_rate,
        isAvailable: actor?.is_available,
        fullName: actor?.user_profiles?.full_name,
        email: actor?.user_profiles?.email,
        createdAt: actor?.created_at,
        updatedAt: actor?.updated_at
      })) || [];
    } catch (err) {
      console.error('Error fetching voice actors:', err);
      throw err;
    }
  },

  async getVoiceActorProfile(voiceActorId) {
    try {
      // Get voice actor details
      const { data: actor, error: actorError } = await supabase?.from('voice_actors')?.select(`
          *,
          user_profiles!user_id (
            full_name,
            email
          )
        `)?.eq('id', voiceActorId)?.single();

      if (actorError) throw actorError;

      // Get assignments with line groups and plays
      const { data: assignments, error: assignmentsError } = await supabase?.from('voice_actor_assignments')?.select(`
          *,
          line_groups (
            id,
            name,
            character_name,
            line_count,
            plays (
              id,
              title,
              cover_image_url
            )
          )
        `)?.eq('voice_actor_id', voiceActorId)?.order('assigned_date', { ascending: false });

      if (assignmentsError) throw assignmentsError;

      return {
        id: actor?.id,
        userId: actor?.user_id,
        stageName: actor?.stage_name,
        bio: actor?.bio,
        headshotUrl: actor?.headshot_url,
        vocalRange: actor?.vocal_range,
        yearsExperience: actor?.years_experience,
        specializations: actor?.specializations || [],
        demoReelUrl: actor?.demo_reel_url,
        hourlyRate: actor?.hourly_rate,
        isAvailable: actor?.is_available,
        fullName: actor?.user_profiles?.full_name,
        email: actor?.user_profiles?.email,
        updatedAt: actor?.updated_at,
        assignments: assignments?.map(assignment => ({
          id: assignment?.id,
          characterName: assignment?.line_groups?.character_name,
          lineGroupName: assignment?.line_groups?.name,
          lineCount: assignment?.line_groups?.line_count,
          playTitle: assignment?.line_groups?.plays?.title,
          playId: assignment?.line_groups?.plays?.id,
          playCoverUrl: assignment?.line_groups?.plays?.cover_image_url,
          status: assignment?.assignment_status,
          assignedDate: assignment?.assigned_date,
          dueDate: assignment?.due_date
        })) || []
      };
    } catch (err) {
      console.error('Error fetching voice actor profile:', err);
      throw err;
    }
  },

  async getAvailability(voiceActorId, startDate, endDate) {
    try {
      const { data, error } = await supabase?.from('voice_actor_availability')?.select('*')?.eq('voice_actor_id', voiceActorId)?.gte('date', startDate)?.lte('date', endDate)?.order('date', { ascending: true });

      if (error) throw error;

      return data?.map(slot => ({
        id: slot?.id,
        voiceActorId: slot?.voice_actor_id,
        date: slot?.date,
        startTime: slot?.start_time,
        endTime: slot?.end_time,
        status: slot?.status,
        notes: slot?.notes
      })) || [];
    } catch (err) {
      console.error('Error fetching availability:', err);
      throw err;
    }
  },

  async getLineGroups(playId = null) {
    try {
      let query = supabase?.from('line_groups')?.select(`
          *,
          plays (
            title
          )
        `)?.order('character_name');

      if (playId) {
        query = query?.eq('play_id', playId);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(group => ({
        id: group?.id,
        playId: group?.play_id,
        playTitle: group?.plays?.title,
        name: group?.name,
        characterName: group?.character_name,
        lineCount: group?.line_count,
        estimatedDurationMinutes: group?.estimated_duration_minutes,
        complexityRating: group?.complexity_rating,
        notes: group?.notes,
        createdAt: group?.created_at
      })) || [];
    } catch (err) {
      console.error('Error fetching line groups:', err);
      throw err;
    }
  },

  async getAssignments(filters = {}) {
    try {
      let query = supabase?.from('voice_actor_assignments')?.select(`
          *,
          voice_actors (
            stage_name,
            vocal_range,
            specializations
          ),
          line_groups (
            name,
            character_name,
            line_count,
            complexity_rating,
            plays (
              title
            )
          )
        `)?.order('created_at', { ascending: false });

      if (filters?.voiceActorId) {
        query = query?.eq('voice_actor_id', filters?.voiceActorId);
      }

      if (filters?.status) {
        query = query?.eq('assignment_status', filters?.status);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(assignment => ({
        id: assignment?.id,
        voiceActorId: assignment?.voice_actor_id,
        lineGroupId: assignment?.line_group_id,
        status: assignment?.assignment_status,
        assignedDate: assignment?.assigned_date,
        dueDate: assignment?.due_date,
        priority: assignment?.priority,
        notes: assignment?.notes,
        voiceActor: {
          stageName: assignment?.voice_actors?.stage_name,
          vocalRange: assignment?.voice_actors?.vocal_range,
          specializations: assignment?.voice_actors?.specializations
        },
        lineGroup: {
          name: assignment?.line_groups?.name,
          characterName: assignment?.line_groups?.character_name,
          lineCount: assignment?.line_groups?.line_count,
          complexityRating: assignment?.line_groups?.complexity_rating,
          playTitle: assignment?.line_groups?.plays?.title
        }
      })) || [];
    } catch (err) {
      console.error('Error fetching assignments:', err);
      throw err;
    }
  },

  async getCharactersByPlay(playId) {
    try {
      const { data, error } = await supabase
        ?.from('lines')
        ?.select('character, play_id, plays!inner(title)')
        ?.eq('play_id', playId)
        ?.eq('is_stage_direction', 'LINE')
        ?.not('character', 'is', null);

      if (error) throw error;

      // Group by character and count lines
      const characterMap = {};
      data?.forEach(line => {
        if (line?.character && line?.character?.trim() !== '') {
          if (!characterMap?.[line?.character]) {
            characterMap[line?.character] = {
              character: line?.character,
              playId: line?.play_id,
              playTitle: line?.plays?.title,
              lineCount: 0
            };
          }
          characterMap[line?.character].lineCount++;
        }
      });

      return Object.values(characterMap)?.sort((a, b) => b?.lineCount - a?.lineCount);
    } catch (err) {
      console.error('Error fetching characters:', err);
      throw err;
    }
  },

  async assignVoiceActorToCharacter(voiceActorId, playId, characterName, assignedBy) {
    try {
      // Get all lines for this character in this play
      const { data: lines, error: linesError } = await supabase
        ?.from('lines')
        ?.select('id')
        ?.eq('play_id', playId)
        ?.eq('character', characterName)
        ?.eq('is_stage_direction', 'LINE');

      if (linesError) throw linesError;

      if (!lines || lines?.length === 0) {
        throw new Error('No lines found for this character');
      }

      // Update all audio_recordings for these lines to assign the voice actor
      // First, check if audio_recordings exist for these lines
      const lineIds = lines?.map(l => l?.id);
      
      const { data: existingRecordings, error: recordingsError } = await supabase
        ?.from('audio_recordings')
        ?.select('id, line_id')
        ?.in('line_id', lineIds);

      if (recordingsError) throw recordingsError;

      // Update existing recordings
      if (existingRecordings && existingRecordings?.length > 0) {
        const { error: updateError } = await supabase
          ?.from('audio_recordings')
          ?.update({ user_id: voiceActorId })
          ?.in('line_id', lineIds);

        if (updateError) throw updateError;
      }

      // Create or update line_group for this character
      const { data: existingGroup, error: groupCheckError } = await supabase
        ?.from('line_groups')
        ?.select('id')
        ?.eq('play_id', playId)
        ?.eq('character_name', characterName)
        ?.single();

      let lineGroupId;

      if (existingGroup) {
        lineGroupId = existingGroup?.id;
      } else {
        // Create new line group
        const { data: newGroup, error: createGroupError } = await supabase
          ?.from('line_groups')
          ?.insert([{
            play_id: playId,
            name: `${characterName} - All Lines`,
            character_name: characterName,
            line_count: lines?.length,
            created_by: assignedBy
          }])
          ?.select()
          ?.single();

        if (createGroupError) throw createGroupError;
        lineGroupId = newGroup?.id;

        // Create line_group_assignments for all lines
        const assignments = lines?.map((line, index) => ({
          line_group_id: lineGroupId,
          line_id: line?.id,
          sequence_order: index + 1
        }));

        const { error: assignmentsError } = await supabase
          ?.from('line_group_assignments')
          ?.insert(assignments);

        if (assignmentsError) throw assignmentsError;
      }

      // Create voice_actor_assignment
      const { data: assignment, error: assignmentError } = await supabase
        ?.from('voice_actor_assignments')
        ?.insert([{
          voice_actor_id: voiceActorId,
          line_group_id: lineGroupId,
          assignment_status: 'pending',
          assigned_by: assignedBy,
          assigned_date: new Date()?.toISOString()?.split('T')?.[0]
        }])
        ?.select()
        ?.single();

      if (assignmentError) throw assignmentError;

      return {
        success: true,
        assignment,
        linesAssigned: lines?.length
      };
    } catch (err) {
      console.error('Error assigning voice actor to character:', err);
      throw err;
    }
  },

  async getCharacterAssignments(playId) {
    try {
      const { data, error } = await supabase
        ?.from('voice_actor_assignments')
        ?.select(`
          *,
          voice_actors (
            id,
            stage_name,
            user_profiles:user_id (
              full_name
            )
          ),
          line_groups!inner (
            character_name,
            play_id,
            line_count
          )
        `)
        ?.eq('line_groups.play_id', playId);

      if (error) throw error;

      return data?.map(assignment => ({
        id: assignment?.id,
        characterName: assignment?.line_groups?.character_name,
        voiceActorId: assignment?.voice_actor_id,
        voiceActorName: assignment?.voice_actors?.stage_name || assignment?.voice_actors?.user_profiles?.full_name,
        lineCount: assignment?.line_groups?.line_count,
        status: assignment?.assignment_status
      })) || [];
    } catch (err) {
      console.error('Error fetching character assignments:', err);
      throw err;
    }
  },

  async createAssignment(assignmentData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('voice_actor_assignments')?.insert({
          voice_actor_id: assignmentData?.voiceActorId,
          line_group_id: assignmentData?.lineGroupId,
          assignment_status: assignmentData?.status || 'pending',
          assigned_by: user?.id,
          due_date: assignmentData?.dueDate,
          priority: assignmentData?.priority || 5,
          notes: assignmentData?.notes
        })?.select()?.single();

      if (error) throw error;
      return data;
    } catch (err) {
      console.error('Error creating assignment:', err);
      throw err;
    }
  },

  async updateAssignment(assignmentId, updates) {
    try {
      const { data, error } = await supabase?.from('voice_actor_assignments')?.update({
          assignment_status: updates?.status,
          due_date: updates?.dueDate,
          priority: updates?.priority,
          notes: updates?.notes
        })?.eq('id', assignmentId)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (err) {
      console.error('Error updating assignment:', err);
      throw err;
    }
  },

  async deleteAssignment(assignmentId) {
    try {
      const { error } = await supabase?.from('voice_actor_assignments')?.delete()?.eq('id', assignmentId);

      if (error) throw error;
    } catch (err) {
      console.error('Error deleting assignment:', err);
      throw err;
    }
  },

  /**
   * Get assignments with upcoming deadlines
   * @param {number} daysThreshold - Number of days to look ahead (default: 3)
   * @returns {Promise<Array>} Assignments with deadlines within threshold
   */
  async getUpcomingDeadlines(daysThreshold = 3) {
    try {
      const today = new Date();
      const futureDate = new Date();
      futureDate?.setDate(today?.getDate() + daysThreshold);

      const { data, error } = await supabase
        ?.from('voice_actor_assignments')
        ?.select(`
          *,
          voice_actors (
            stage_name,
            user_profiles:user_id (
              email,
              full_name
            )
          ),
          line_groups (
            name,
            character_name,
            line_count,
            plays (
              title
            )
          )
        `)
        ?.gte('due_date', today?.toISOString()?.split('T')?.[0])
        ?.lte('due_date', futureDate?.toISOString()?.split('T')?.[0])
        ?.in('assignment_status', ['pending', 'in_progress'])
        ?.order('due_date', { ascending: true });

      if (error) throw error;

      return data?.map(assignment => {
        const dueDate = new Date(assignment?.due_date);
        const daysRemaining = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));

        return {
          id: assignment?.id,
          voiceActorId: assignment?.voice_actor_id,
          lineGroupId: assignment?.line_group_id,
          status: assignment?.assignment_status,
          dueDate: assignment?.due_date,
          priority: assignment?.priority,
          notes: assignment?.notes,
          daysRemaining,
          voiceActor: {
            stageName: assignment?.voice_actors?.stage_name,
            email: assignment?.voice_actors?.user_profiles?.email,
            fullName: assignment?.voice_actors?.user_profiles?.full_name
          },
          lineGroup: {
            name: assignment?.line_groups?.name,
            characterName: assignment?.line_groups?.character_name,
            lineCount: assignment?.line_groups?.line_count,
            playTitle: assignment?.line_groups?.plays?.title
          }
        };
      }) || [];
    } catch (err) {
      console.error('Error fetching upcoming deadlines:', err);
      throw err;
    }
  },

  async getRecordingSessions(filters = {}) {
    try {
      let query = supabase?.from('recording_sessions')?.select(`
          *,
          voice_actors (
            stage_name
          ),
          recording_session_templates (
            name,
            session_type
          )
        `)?.order('scheduled_date', { ascending: true });

      if (filters?.voiceActorId) {
        query = query?.eq('voice_actor_id', filters?.voiceActorId);
      }

      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(session => ({
        id: session?.id,
        templateId: session?.template_id,
        voiceActorId: session?.voice_actor_id,
        scheduledDate: session?.scheduled_date,
        startTime: session?.start_time,
        endTime: session?.end_time,
        lineGroups: session?.line_groups || [],
        location: session?.location,
        status: session?.status,
        notes: session?.notes,
        voiceActorName: session?.voice_actors?.stage_name,
        templateName: session?.recording_session_templates?.name,
        sessionType: session?.recording_session_templates?.session_type
      })) || [];
    } catch (err) {
      console.error('Error fetching recording sessions:', err);
      throw err;
    }
  },

  async createRecordingSession(sessionData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('recording_sessions')?.insert({
          template_id: sessionData?.templateId,
          voice_actor_id: sessionData?.voiceActorId,
          scheduled_date: sessionData?.scheduledDate,
          start_time: sessionData?.startTime,
          end_time: sessionData?.endTime,
          line_groups: sessionData?.lineGroups || [],
          location: sessionData?.location,
          status: sessionData?.status || 'pending',
          notes: sessionData?.notes,
          created_by: user?.id
        })?.select()?.single();

      if (error) throw error;
      return data;
    } catch (err) {
      console.error('Error creating recording session:', err);
      throw err;
    }
  },

  async getSessionTemplates() {
    try {
      const { data, error } = await supabase?.from('recording_session_templates')?.select('*')?.order('name');

      if (error) throw error;

      return data?.map(template => ({
        id: template?.id,
        name: template?.name,
        sessionType: template?.session_type,
        description: template?.description,
        estimatedDurationMinutes: template?.estimated_duration_minutes,
        linesPerHour: template?.lines_per_hour,
        setupRequirements: template?.setup_requirements || []
      })) || [];
    } catch (err) {
      console.error('Error fetching session templates:', err);
      throw err;
    }
  },

  async getStatistics() {
    try {
      const [actorsResult, assignmentsResult, sessionsResult] = await Promise.all([
        supabase?.from('voice_actors')?.select('id', { count: 'exact', head: true }),
        supabase?.from('voice_actor_assignments')?.select('id', { count: 'exact', head: true }),
        supabase?.from('recording_sessions')?.select('id', { count: 'exact', head: true })
      ]);

      return {
        totalActors: actorsResult?.count || 0,
        totalAssignments: assignmentsResult?.count || 0,
        totalSessions: sessionsResult?.count || 0
      };
    } catch (err) {
      console.error('Error fetching statistics:', err);
      throw err;
    }
  }
};

// Follow/Unfollow Voice Actor
export const followVoiceActor = async (voiceActorId) => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase?.from('voice_actor_followers')?.insert({
        voice_actor_id: voiceActorId,
        follower_user_id: user?.id
      })?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error following voice actor:', error);
    throw error;
  }
};

export const unfollowVoiceActor = async (voiceActorId) => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('User not authenticated');

    const { error } = await supabase?.from('voice_actor_followers')?.delete()?.eq('voice_actor_id', voiceActorId)?.eq('follower_user_id', user?.id);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error unfollowing voice actor:', error);
    throw error;
  }
};

export const getFollowerCount = async (voiceActorId) => {
  try {
    const { count, error } = await supabase?.from('voice_actor_followers')?.select('*', { count: 'exact', head: true })?.eq('voice_actor_id', voiceActorId);

    if (error) throw error;
    return count || 0;
  } catch (error) {
    console.error('Error getting follower count:', error);
    return 0;
  }
};

export const isFollowing = async (voiceActorId) => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) return false;

    const { data, error } = await supabase?.from('voice_actor_followers')?.select('id')?.eq('voice_actor_id', voiceActorId)?.eq('follower_user_id', user?.id)?.maybeSingle();

    if (error) throw error;
    return !!data;
  } catch (error) {
    console.error('Error checking follow status:', error);
    return false;
  }
};

export const getMyFollowerCount = async () => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) return 0;

    // Get voice actor record for current user
    const { data: voiceActor, error: actorError } = await supabase?.from('voice_actors')?.select('id')?.eq('user_id', user?.id)?.maybeSingle();

    if (actorError) throw actorError;
    if (!voiceActor) return 0;

    // Get follower count
    const { count, error } = await supabase?.from('voice_actor_followers')?.select('*', { count: 'exact', head: true })?.eq('voice_actor_id', voiceActor?.id);

    if (error) throw error;
    return count || 0;
  } catch (error) {
    console.error('Error getting my follower count:', error);
    return 0;
  }
};